import { computed, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { STORAGE_KEY } from '@cpd-app/core-module/constants/storage-key.const';
import {
  SHOW_ANIMATION_HOME_PAGE_TIMER,
  SHOW_ANIMATION_QUICK_MATCH_TIMER
} from '@cpd-app/core-module/constants/timer.const';
import { OccupationDetail, ViaPageContext } from '@cpd-app/core-module/models';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { EMPTY, Observable, timer } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ViaNudgesService {
  private readonly currentOccupation$ = signal<OccupationDetail>(null);
  private readonly isOpenViaOnce$ = signal(false);
  private readonly unSentMessage$ = signal<string>('');
  private readonly visitOccupationOnce$ = signal(false);
  /**
   * VIA-28 section 3.2. The page the learner is on, for Via. Page containers
   * set it on init and clear it on destroy; the stream effect sends it on
   * every turn (a question's own page wins).
   */
  private readonly currentPage$ = signal<ViaPageContext | null>(null);

  public readonly currentOccupation = computed(() => this.currentOccupation$());
  public readonly unSentMessage = computed(() => this.unSentMessage$());
  public readonly visitOccupationOnce = computed(() =>
    this.visitOccupationOnce$()
  );
  public readonly isViaOpenOnce = computed(() => this.isOpenViaOnce$());
  public readonly currentPage = computed(() => this.currentPage$());

  public constructor(
    private readonly route: Router,
    private readonly cognitoAuthFacade: CognitoAuthFacade
  ) {
    const unSentMessage = sessionStorage.getItem(STORAGE_KEY.UN_SENT_MESSAGE);
    this.unSentMessage$.set(unSentMessage);
  }

  public setCurrentOccupation(occ: OccupationDetail): void {
    this.currentOccupation$.set(occ);
  }

  public setCurrentPage(page: ViaPageContext | null): void {
    this.currentPage$.set(page);
  }

  /**
   * Clears the page only if it is still the one this caller set. Leaving one
   * page for another can destroy the old container after the new one has set
   * its page, and that late clear must not wipe the new page.
   */
  public clearCurrentPage(page: ViaPageContext | null): void {
    if (page && this.currentPage$() === page) {
      this.currentPage$.set(null);
    }
  }

  public setIsOpenViaOnce(): void {
    this.isOpenViaOnce$.set(true);
  }

  public setVisitOccupationOnce(): void {
    this.visitOccupationOnce$.set(true);
  }

  public setUnSentMessage(message: string): void {
    this.unSentMessage$.set(message);
    sessionStorage.setItem(STORAGE_KEY.UN_SENT_MESSAGE, message);
  }

  public clearMessage(): void {
    this.unSentMessage$.set('');
    sessionStorage.removeItem(STORAGE_KEY.UN_SENT_MESSAGE);
  }

  public applyAnimation(): Observable<number> {
    return this.route.events.pipe(
      filter((e) => e instanceof NavigationEnd && !this.isOpenViaOnce$()),
      switchMap((e: NavigationEnd) => {
        // /home is the landing page now; build-your-target is kept for the
        // old page, which is still reachable.
        const isHomePage =
          e.url.includes('build-your-target') || e.url.startsWith('/home');
        const isQuickMatch = e.url.includes('quick-match');

        return isHomePage
          ? timer(SHOW_ANIMATION_HOME_PAGE_TIMER)
          : isQuickMatch
            ? timer(SHOW_ANIMATION_QUICK_MATCH_TIMER)
            : EMPTY;
      })
    );
  }
}
